<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="d-flex flex-wrap align-center mb-3">
        <h2 class="body-1 mr-auto kvm-card-title">
          <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-docker</v-icon>
          <span class="ml-2">Docker</span>
          <span v-if="status && status.available" class="kvm-help text-caption ml-2">
            Engine {{ status.version }}<template v-if="status.compose_version"> · Compose {{ status.compose_version }}</template>
          </span>
        </h2>
      </div>

      <!-- The app chosen with this server (from Applications), installed once it's built. -->
      <v-alert v-if="appInstall && ['pending', 'installing'].includes(appInstall.state)" type="info" dense text class="mb-3 kvm-dk-alert" role="status">
        Installing {{ appInstall.name }}… it was chosen with this server and is installed automatically once the server is ready.
      </v-alert>
      <v-alert v-else-if="appInstall && appInstall.state === 'failed'" type="warning" dense text class="mb-3 kvm-dk-alert">
        {{ appInstall.error || `${appInstall.name} couldn't be installed.` }} You can deploy it again under Apps.
      </v-alert>

      <v-alert v-if="!server.docker_enabled" type="info" border="left" dense class="mb-0">
        Docker management isn't available for this server.
      </v-alert>

      <v-alert v-else-if="server.power_state && server.power_state !== 'online'" type="info" border="left" dense class="mb-0">
        This server is {{ server.power_state }}. Power it on to manage Docker.
      </v-alert>

      <template v-else>
        <div v-if="checking" class="py-8 text-center" role="status" aria-live="polite">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
          <span class="kvm-sr-only">Checking Docker…</span>
        </div>

        <v-alert v-else-if="!status || !status.available" type="warning" border="left" dense role="alert" class="mb-0">
          {{ unavailableText }}
          <div class="mt-2">
            <gg-btn small tile outlined color="primary" @click="check">
              <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Check again
            </gg-btn>
          </div>
        </v-alert>

        <template v-else>
          <!-- Running / recently finished background operations -->
          <kvm-docker-operation
            v-for="op in operations" :key="op.id"
            :service-id="server.id" :operation="op"
            @done="onOperationDone" @dismiss="dismiss(op)"
          />

          <v-tabs v-model="tab" class="mb-3" show-arrows>
            <v-tab>Apps</v-tab>
            <v-tab>Containers</v-tab>
            <v-tab>Domains</v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <!-- Apps = compose stacks; "Deploy new app" opens the template catalog + custom. -->
              <kvm-docker-stacks :service-id="server.id" :refresh-key="refreshKey" :finished-ops="finishedOps" @operation="addOperation" />
            </v-tab-item>
            <v-tab-item>
              <kvm-docker-containers :service-id="server.id" :refresh-key="refreshKey" @operation="addOperation" />
            </v-tab-item>
            <v-tab-item>
              <kvm-docker-proxy :service-id="server.id" :refresh-key="refreshKey" @operation="addOperation" />
            </v-tab-item>
          </v-tabs-items>
        </template>
      </template>
    </gg-card-text>
  </gg-card>
</template>

<script>
import kvm from '@/api/kvm'
import KvmDockerOperation from '@/views/services/hosting/kvm/KvmDockerOperation'
import KvmDockerContainers from '@/views/services/hosting/kvm/KvmDockerContainers'
import KvmDockerStacks from '@/views/services/hosting/kvm/KvmDockerStacks'
import KvmDockerProxy from '@/views/services/hosting/kvm/KvmDockerProxy'

const UNAVAILABLE = {
  agent_unavailable: 'The server isn’t responding to management requests yet. If it just started, give it a minute and check again.',
  docker_not_installed: 'Docker isn’t installed on this server.',
  daemon_down: 'Docker is installed but not running on this server. Start it with “systemctl start docker” in the Terminal.',
  timeout: 'The server took too long to respond. Check again in a moment.',
}

export default {
  name: 'HostingKvmDocker',
  components: { KvmDockerOperation, KvmDockerContainers, KvmDockerStacks, KvmDockerProxy },
  props: {
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    status: null,
    checking: false,
    tab: 0,
    operations: [],
    // Ids of operations that have finished (lists clear their "Deleting…" etc.).
    finishedOps: [],
    refreshKey: 0,
  }),
  computed: {
    appInstall () {
      return this.server.docker_app_install || null
    },
    unavailableText () {
      return (this.status && UNAVAILABLE[this.status.reason]) || 'Docker is not available on this server right now.'
    },
  },
  methods: {
    async check () {
      if (!this.server.id || !this.server.docker_enabled) return
      this.checking = true
      try {
        this.status = await kvm.dockerStatus(this.server.id)
      } catch (e) {
        this.status = { available: false, reason: e.status === 503 ? 'agent_unavailable' : null }
      } finally {
        this.checking = false
      }
    },
    addOperation (op) {
      if (op && op.id) this.operations.unshift(op)
    },
    onOperationDone (result) {
      if (result && result.id) this.finishedOps = [...this.finishedOps, result.id]
      // Refresh the lists so the result of the operation shows up.
      this.refreshKey++
    },
    dismiss (op) {
      this.operations = this.operations.filter(o => o.id !== op.id)
    },
  },
  watch: {
    'server.id': { immediate: true, handler () { this.check() } },
    'server.power_state' (state, old) {
      if (state === 'online' && old && old !== 'online') this.check()
    },
    // The app chosen with the server just finished → show it in the list.
    'server.docker_app_install.state' (state, old) {
      if (old && state !== old) this.refreshKey++
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-card-title { margin: 0; }
.kvm-dk-alert ::v-deep .v-alert__content { color: #212121 !important; }
.theme--dark .kvm-dk-alert ::v-deep .v-alert__content { color: #eceff1 !important; }
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
